import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { goBackOrParent } from "@/lib/nav";
import { QRCodeCanvas } from "qrcode.react";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, imgUrl, photoThumb, photoFull } from "@/lib/api";
import { uploadPhotos } from "@/lib/photoUpload";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { ProgressRing } from "@/components/ProgressRing";
import { PunchDispatch } from "@/components/PunchDispatch";
import { NudgeSheet } from "@/components/NudgeSheet";
import { timeAgo, countryFlag } from "@/lib/format";
import {
  ArrowLeft, Trash2, Loader2, Send, Link2, Copy, X, Check, Sparkles, Plus,
  Eye, MessageSquare, Mail, Image as ImageIcon, Globe, Camera, Megaphone, ChevronDown, ChevronRight, AlertTriangle, Share2, CalendarClock, FileText, Bell,
} from "lucide-react";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { ReplacePhotoButton } from "@/components/ReplacePhotoButton";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { buildReportPdfBlob } from "@/lib/reportPdf";

// Grow a textarea to fit its content so long titles wrap and stay fully visible.
const autoGrow = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const STATUS_OPTIONS = [
  ["pending", "Pending"],
  ["done", "Complete"],
  ["issue", "Issue"],
  ["na", "N/A"],
];
const STATUS_ACTIVE = {
  done: "border-emerald-500 bg-emerald-500/15 text-emerald-400",
  issue: "border-rose-500 bg-rose-500/15 text-rose-400",
  na: "border-slate-500 bg-slate-500/15 text-slate-200",
  pending: "border-amber-500 bg-amber-500/15 text-amber-400",
};

// Resilient thumbnail: starts on the (possibly presigned, possibly stale-from-cache)
// URL, and on load failure falls back to the /api/img proxy which re-signs R2
// server-side — so an expired cached link recovers without a remount.
function SmartPhoto({ photo, onOpen, onAdd, onReplaced, frameClass, emptyIcon: EmptyIcon, emptyLabel, testId, emptyTestId }) {
  const primary = photoThumb(photo);
  const proxy = photo && (photo.thumbnail_cloud_path || photo.cloud_path)
    ? imgUrl(photo.thumbnail_cloud_path || photo.cloud_path, 400)
    : null;
  const [src, setSrc] = useState(primary);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setSrc(primary); setFailed(false); }, [primary]);

  if (!photo || failed || !src) {
    if (onAdd && !photo) {
      return (
        <button
          type="button"
          data-testid={emptyTestId}
          onClick={onAdd}
          className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-emerald-500/50 bg-emerald-500/5 text-center text-[11px] font-semibold text-emerald-500 transition-all active:scale-[0.98] hover:bg-emerald-500/10"
        >
          <EmptyIcon className="h-5 w-5" />
          {emptyLabel}
          <span className="text-[10px] font-normal text-emerald-500/70">Tap to add</span>
        </button>
      );
    }
    return (
      <div
        data-testid={emptyTestId}
        className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-muted/30 text-center text-[11px] text-muted-foreground"
      >
        <EmptyIcon className="h-5 w-5 opacity-40" />{photo ? "Unavailable" : emptyLabel}
        {photo && onReplaced && <ReplacePhotoButton photo={photo} onReplaced={onReplaced} testId={`${testId}-replace`} />}
      </div>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(photoFull(photo))} className={frameClass} data-testid={testId}>
      <img
        src={src}
        alt=""
        onError={() => { if (proxy && src !== proxy) setSrc(proxy); else setFailed(true); }}
        className="h-full w-full object-cover transition-transform duration-300 group-active:scale-[1.04]"
      />
    </button>
  );
}

function ItemCard({ item, onPatch, onDelete, onOpenPhoto, onAddProof, onPhotoReplaced, index = 0 }) {
  const [title, setTitle] = useState(item.title || "");
  const refPhoto = (item.photos || []).find((p) => p.kind !== "proof");
  const proofPhoto = (item.photos || []).find((p) => p.kind === "proof");
  const verified = item.status === "done";
  const hasProof = !!proofPhoto;
  const awaiting = !verified && (hasProof || item.status === "sub_done");
  // Build a swipeable gallery so tapping either photo lets you swipe Reference ↔ Proof.
  const gallery = [
    refPhoto && { url: photoFull(refPhoto), label: "Reference", photo: refPhoto, itemId: item.id },
    proofPhoto && { url: photoFull(proofPhoto), label: "Proof", photo: proofPhoto, itemId: item.id },
  ].filter(Boolean);
  const openFromUrl = (url) => onOpenPhoto({ photos: gallery, index: Math.max(0, gallery.findIndex((g) => g.url === url)) });

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.32, delay: Math.min(index * 0.05, 0.3), ease: [0.22, 0.61, 0.36, 1] }}
      className={cn(
        "card-elevated relative overflow-hidden rounded-2xl border bg-card p-4 transition-colors",
        verified ? "border-emerald-500/40" : awaiting ? "border-amber-500/50" : "border-border"
      )}
      data-testid={`item-card-${item.id}`}
    >
      {/* status accent stripe down the left edge */}
      <span className={cn(
        "absolute inset-y-0 left-0 w-1",
        verified ? "bg-emerald-500" : awaiting ? "bg-amber-500" : "bg-transparent"
      )} />

      <div className="flex items-start gap-3">
        {/* Owner verify check-circle: green when verified, tap to toggle */}
        <button
          type="button"
          data-testid={`item-verify-${item.id}`}
          onClick={() => onPatch(item.id, { status: verified ? "pending" : "done" })}
          aria-label={verified ? "Verified — tap to undo" : "Verify complete"}
          className={cn(
            "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-all active:scale-90",
            verified
              ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
              : "border-muted-foreground/30 text-transparent hover:border-emerald-500 hover:text-emerald-500"
          )}
        >
          <Check className="h-4 w-4" strokeWidth={3} />
        </button>

        <div className="min-w-0 flex-1">
          <textarea
            data-testid={`item-title-${item.id}`}
            value={title}
            rows={1}
            ref={autoGrow}
            onInput={(e) => autoGrow(e.target)}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title !== item.title && onPatch(item.id, { title })}
            placeholder="What needs to be done?"
            className={cn(
              "w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-[15px] font-semibold leading-snug shadow-none outline-none placeholder:text-muted-foreground focus:ring-0",
              verified && "text-muted-foreground line-through decoration-emerald-500/40"
            )}
          />
          {verified ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-500" data-testid={`item-verified-${item.id}`}>
              <Check className="h-3 w-3" /> Verified
            </span>
          ) : awaiting ? (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-500" data-testid={`item-awaiting-${item.id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Proof in — tap ✓ to verify
            </span>
          ) : item.status === "issue" ? (
            <div className="mt-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-500">Issue flagged</span>
              {item.description && <p className="mt-1.5 rounded-lg bg-rose-500/10 px-2.5 py-1.5 text-[13px] leading-snug text-rose-600 dark:text-rose-300" data-testid={`item-issue-note-${item.id}`}>{item.description}</p>}
            </div>
          ) : (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Pending proof</span>
          )}
        </div>

        <button data-testid={`item-delete-${item.id}`} onClick={() => onDelete(item.id)} className="-mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:bg-rose-500/10 hover:text-rose-500">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Before → After: owner reference vs worker proof, split by a divider */}
      <div className="mt-4 flex items-stretch pl-10">
        <figure className="min-w-0 flex-1">
          <figcaption className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" /> Reference
          </figcaption>
          <div className="relative">
            <SmartPhoto
              photo={refPhoto}
              onOpen={openFromUrl}
              onReplaced={(p) => onPhotoReplaced(item.id, p)}
              frameClass="group block aspect-square w-full overflow-hidden rounded-xl border border-border ring-1 ring-black/5"
              emptyIcon={ImageIcon}
              emptyLabel="No photo"
              testId={`item-ref-${item.id}`}
            />
          </div>
        </figure>

        {/* separator with directional chip */}
        <div className="relative mx-3 flex w-px shrink-0 items-center justify-center self-stretch bg-border">
          <span className={cn(
            "flex h-6 w-6 items-center justify-center rounded-full border bg-card shadow-sm",
            hasProof ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground"
          )}>
            <ChevronRight className="h-3.5 w-3.5" />
          </span>
        </div>

        <figure className="min-w-0 flex-1">
          <figcaption className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Proof
          </figcaption>
          <div className="relative">
            <SmartPhoto
              photo={proofPhoto}
              onOpen={openFromUrl}
              onAdd={() => onAddProof(item.id)}
              onReplaced={(p) => onPhotoReplaced(item.id, p)}
              frameClass="group block aspect-square w-full overflow-hidden rounded-xl border-2 border-emerald-500/60 ring-1 ring-emerald-500/10"
              emptyIcon={Camera}
              emptyLabel="Add proof"
              testId={`item-proof-${item.id}`}
              emptyTestId={`item-proof-empty-${item.id}`}
            />
          </div>
          {proofPhoto?.caption && (
            <p
              data-testid={`item-proof-caption-${item.id}`}
              className="mt-1.5 flex items-start gap-1 rounded-lg bg-emerald-500/10 px-2 py-1.5 text-[12px] leading-snug text-emerald-100"
            >
              <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
              <span className="min-w-0">{proofPhoto.caption}</span>
            </p>
          )}
        </figure>
      </div>
    </motion.div>
  );
}

function ShareSheet({ open, onOpenChange, reportId, initialStats, jobName, location, dueDate, autoNudge, onNudgeHandled }) {
  const [stats, setStats] = useState(initialStats);
  const [bump, setBump] = useState(false);
  const prev = useRef(initialStats?.total || 0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/${reportId}/views`);
      if ((data.total || 0) !== prev.current) { setBump(true); setTimeout(() => setBump(false), 400); prev.current = data.total; }
      setStats(data);
    } catch { /* ignore */ }
  }, [reportId]);

  useEffect(() => {
    if (!open) return;
    let t = null;
    const start = () => { if (!t) t = setInterval(refresh, 12000); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    const onVis = () => { if (document.hidden) { stop(); } else { refresh(); start(); } };
    refresh();
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [open, refresh]);

  const token = stats?.token || initialStats?.token;
  const url = token ? `${window.location.origin}/api/s/${token}` : (stats?.url || initialStats?.url || "");
  const markSent = () => { api.post(`/reports/${reportId}/mark-sent`).catch(() => {}); };
  const copy = () => { navigator.clipboard.writeText(url); markSent(); toast.success("Link copied"); };
  const dueSuffix = dueDate ? `\n\nPlease complete by ${format(new Date(`${String(dueDate).slice(0, 10)}T00:00:00`), "EEE, MMM d")}.` : "";
  const shareBody = `Punch list — ${location || jobName || "your proof"}.${dueSuffix}\n\nOpen the list and snap a quick proof photo on each item as you finish. Tap "Send proof back" when you're done and I'll get it instantly.\n\n📸 A photo on each item is what closes it out.\n\nForward this to anyone on your crew helping out.`;
  const nativeShare = async () => {
    markSent();
    if (navigator.share) { try { await navigator.share({ text: shareBody, url }); return; } catch { /* cancelled */ } }
    window.location.href = `sms:?&body=${encodeURIComponent(shareBody + "\n" + url)}`;
  };
  const [pdfBusy, setPdfBusy] = useState(false);
  const sharePdf = async () => {
    setPdfBusy(true);
    try {
      // Build the PDF entirely in the browser — no long server request to time
      // out on big, photo-heavy reports.
      const blob = await buildReportPdfBlob(reportId);
      const fname = `${(jobName || "report").replace(/[^\w.-]+/g, "_")}.pdf`;
      const file = new File([blob], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        markSent();
        await navigator.share({ files: [file], title: fname });
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = u; a.download = fname; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 15000);
        toast.success("PDF downloaded");
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) toast.error("Couldn't build the PDF");
    } finally { setPdfBusy(false); }
  };

  const [nudgeOpen, setNudgeOpen] = useState(false);
  useEffect(() => { if (open && autoNudge) { setNudgeOpen(true); onNudgeHandled?.(); } }, [open, autoNudge]); // eslint-disable-line

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[92dvh] overflow-y-auto rounded-t-2xl" data-testid="share-sheet">
        <SheetHeader><SheetTitle>Share this proof</SheetTitle></SheetHeader>

        <div className="mt-4 flex justify-center">
          {url && <div className="rounded-xl border border-slate-200 bg-white p-3"><QRCodeCanvas value={url} size={148} fgColor="#0f172a" /></div>}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Input readOnly value={url} data-testid="share-url-input" className="h-11 bg-background text-xs" />
          <Button size="icon" data-testid="share-copy-btn" onClick={copy} className="h-11 w-11 shrink-0 bg-emerald-500 hover:bg-emerald-600"><Copy className="h-4 w-4" /></Button>
        </div>

        <Button data-testid="share-native-btn" onClick={nativeShare} className="mt-3 h-11 w-full gap-1 bg-emerald-500 hover:bg-emerald-600"><Send className="h-4 w-4" /> Share via…</Button>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <Button variant="outline" data-testid="share-copy-btn-2" onClick={copy} className="h-11 gap-1"><Link2 className="h-4 w-4" /> Copy</Button>
          <a href={`sms:?&body=${encodeURIComponent(shareBody + "\n\n" + url)}`} onClick={markSent} data-testid="share-sms-btn" className="flex h-11 items-center justify-center gap-1 rounded-md border border-border text-sm font-medium"><MessageSquare className="h-4 w-4" /> SMS</a>
          <a href={`mailto:?subject=${encodeURIComponent(`Punch list${jobName ? " — " + jobName : ""}`)}&body=${encodeURIComponent(shareBody + "\n\n" + url)}`} onClick={markSent} data-testid="share-email-btn" className="flex h-11 items-center justify-center gap-1 rounded-md border border-border text-sm font-medium"><Mail className="h-4 w-4" /> Email</a>
        </div>

        <Button variant="outline" data-testid="share-pdf-btn" onClick={sharePdf} disabled={pdfBusy} className="mt-2 h-11 w-full gap-1">
          {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Share as PDF
        </Button>

        <Button data-testid="nudge-worker-btn" onClick={() => setNudgeOpen(true)} className="mt-2 h-11 w-full gap-1 bg-amber-500 text-white hover:bg-amber-600">
          <Bell className="h-4 w-4" /> Nudge worker
        </Button>

        {/* live tracking */}
        <div className="mt-5 rounded-xl border border-border bg-background p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Eye className="h-4 w-4 text-emerald-500" /> Open tracking</div>
            <span data-testid="live-view-count" className={cn("font-heading text-2xl font-bold text-foreground", bump && "animate-count-pop")}>{stats?.total ?? 0}</span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            <span>Last: {stats?.last_opened_at ? timeAgo(stats.last_opened_at) : "not yet"}</span>
            {stats?.last_country && <span className="flex items-center gap-1">{countryFlag(stats.last_country)} {stats.last_country}</span>}
          </div>

          <div className="mt-3 space-y-1.5" data-testid="activity-feed">
            {(stats?.views || []).length === 0 ? (
              <div className="py-2 text-center text-xs text-muted-foreground">No opens yet — send the link!</div>
            ) : (
              stats.views.map((v) => (
                <div key={v.id} className="flex items-center justify-between rounded-lg bg-card px-3 py-2 text-xs">
                  <span className="flex items-center gap-2 text-muted-foreground"><Globe className="h-3.5 w-3.5 text-muted-foreground" /> {v.browser || "Browser"} · {v.os || ""}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">{countryFlag(v.ip_country)} {timeAgo(v.viewed_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <NudgeSheet open={nudgeOpen} onOpenChange={setNudgeOpen} reportId={reportId} jobName={jobName} location={location} dueDate={dueDate} urlOverride={url} />
    </>
  );
}

function DueChip({ dueDate, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = dueDate ? new Date(`${String(dueDate).slice(0, 10)}T00:00:00`) : undefined;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = selected && selected < today;
  const pick = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    onChange(iso); setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="report-due-chip"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors",
            selected ? (overdue ? "bg-rose-500/15 text-rose-500" : "bg-emerald-500/15 text-emerald-500") : "bg-muted text-foreground hover:bg-muted/70"
          )}
        >
          <CalendarClock className="h-4 w-4" />
          {selected ? (overdue ? `Overdue · ${format(selected, "MMM d")}` : `Due ${format(selected, "MMM d")}`) : "Set due date"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid="report-due-popover">
        <Calendar mode="single" selected={selected} onSelect={pick} initialFocus />
        {selected && (
          <button
            data-testid="report-due-clear"
            onClick={() => { onChange(""); setOpen(false); }}
            className="flex w-full items-center justify-center gap-1 border-t border-border py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" /> Clear due date
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const SEND_LANGS = [
  { code: "", label: "English (original)" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "ht", label: "Kreyòl Ayisyen" },
  { code: "fr", label: "Français" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "zh", label: "中文 (简体)" },
  { code: "ru", label: "Русский" },
  { code: "ko", label: "한국어" },
  { code: "pl", label: "Polski" },
];

// Non-destructive: your text stays in English; the worker's share page opens in this language.
function SendLangSelect({ reportId, value, onChange }) {
  const [saving, setSaving] = useState(false);
  const change = async (l) => {
    onChange(l);
    setSaving(true);
    try { await api.post(`/reports/${reportId}/send-lang`, { lang: l }); }
    catch { toast.error("Couldn't save language"); }
    finally { setSaving(false); }
  };
  const label = SEND_LANGS.find((x) => x.code === value)?.label;
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="send-lang-row">
      <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
      <select
        data-testid="send-lang-select"
        value={value}
        onChange={(e) => change(e.target.value)}
        className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm font-medium focus:border-emerald-500 focus:outline-none"
      >
        {SEND_LANGS.map((x) => <option key={x.code || "en"} value={x.code}>{x.code ? `Send in ${x.label}` : x.label}</option>)}
      </select>
      {saving
        ? <Loader2 className="h-4 w-4 animate-spin text-emerald-500" data-testid="send-lang-saving" />
        : value
          ? <span className="text-xs text-muted-foreground" data-testid="send-lang-hint">Worker opens this in <span className="font-semibold text-emerald-500">{label}</span></span>
          : null}
    </div>
  );
}

export default function ReportEditor() {
  const { id, reportId } = useParams();
  const { user: authUser, refresh: refreshAuth } = useAuth();
  const isSandbox = authUser?.subscription?.status === "sandbox";
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [nudgeAuto, setNudgeAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendLang, setSendLang] = useState("");
  const reportPhotoRef = useRef(null);
  const [annot, setAnnot] = useState(null);
  const [dispatch, setDispatch] = useState(false);
  const [addSheet, setAddSheet] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [sendSheet, setSendSheet] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [baPdfBusy, setBaPdfBusy] = useState(false);
  const footerRef = useRef(null);
  const scrollToAdd = () => setTimeout(() => footerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 120);
  const openAddPhoto = (target = null) => { setPhotoTarget(target); setAddSheet(true); };
  const pickPhotos = (e) => {
    setAddSheet(false);
    if (e.target.files?.length) {
      if (photoTarget) setAnnot({ files: Array.from(e.target.files), mode: "item", itemId: photoTarget.itemId, kind: photoTarget.kind, replaceId: photoTarget.replaceId });
      else addPhotoItems(e.target.files);
    }
    e.target.value = "";
  };


  const load = useCallback(async () => {
    const apply = (data) => { setReport(data.report); setJob(data.job); setItems(data.items || []); setStats(data.stats); setTitle(data.report.title); setSendLang(data.send_lang || ""); };
    try {
      // Prefer the server when online so a report never shows a STALE cached
      // snapshot (e.g. a freshly-sent report with items appearing as an empty
      // Draft). Offline still falls back to the IndexedDB cache below.
      if (navigator.onLine) {
        const res = await api.raw.get(`/reports/${reportId}`);
        apply(res.data);
        api.getCached(`/reports/${reportId}`).catch(() => {}); // keep offline cache warm
      } else {
        const { data } = await api.getCached(`/reports/${reportId}`, apply);
        if (data) apply(data);
        else { toast.error("Report not found"); navigate(`/jobs/${id}`); }
      }
    } catch {
      try {
        const { data } = await api.getCached(`/reports/${reportId}`, apply);
        if (data) apply(data);
        else { toast.error("Report not found"); navigate(`/jobs/${id}`); }
      } catch { toast.error("Report not found"); navigate(`/jobs/${id}`); }
    } finally { setLoading(false); }
  }, [reportId, id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (report && !stats?.token) {
      api.post(`/reports/${reportId}/share`).then(({ data }) => setStats(data.stats)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  // Conversion moment: the first time a no-signup (sandbox) owner builds a real
  // list (their one free job now has items worth keeping), gently ask for an
  // email to SAVE it. Fires once ever, then the SandboxBanner carries the nudge.
  useEffect(() => {
    if (authUser?.subscription?.status !== "sandbox") return;
    if (items.length < 1) return;
    try { if (localStorage.getItem("sp_save_prompt_done")) return; } catch { /* ignore */ }
    try { localStorage.setItem("sp_save_prompt_done", "1"); } catch { /* ignore */ }
    const t = setTimeout(() => window.dispatchEvent(new Event("sp:convert")), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.subscription?.status, items.length]);

  const patchItem = async (itemId, fields) => {
    setItems((arr) => arr.map((it) => (it.id === itemId ? { ...it, ...fields } : it)));
    try { await api.patch(`/items/${itemId}`, fields); } catch { toast.error("Save failed"); }
  };
  const deleteItem = async (itemId) => {
    setItems((arr) => arr.filter((it) => it.id !== itemId));
    try { await api.delete(`/items/${itemId}`); } catch { toast.error("Delete failed"); }
  };
  const handleUploadErr = (e) => {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 402 && typeof d === "string" && d.startsWith("sandbox")) {
      window.dispatchEvent(new Event("sp:convert"));
    } else { toast.error("Upload failed"); }
  };
  const addPhotoItems = (files) => {
    const arr = Array.from(files || []);
    if (arr.length) setAnnot({ files: arr, mode: "new-item" });
  };
  const onAddItemPhotos = (itemId, files) => setAnnot({ files: Array.from(files), mode: "item", itemId });
  const handleAnnotSave = async (blob, note) => {
    if (annot?.mode === "new-item") {
      try {
        const { data: item } = await api.post(`/reports/${reportId}/items`, { title: (note || "").slice(0, 60), status: "pending", notes: note || "", order: items.length });
        setItems((arr) => [...arr, { ...item, photos: [] }]);
        scrollToAdd();
        // upload in the background so the annotator closes instantly
        uploadPhotos([blob], { endpoint: "/photos/upload", fields: { item_id: item.id, kind: "reference", caption: note || "" }, onEach: (p) => { addPhoto(item.id, p); if (isSandbox) refreshAuth(); } }).catch(handleUploadErr);
      } catch { toast.error("Couldn't add item"); }
    } else if (annot?.itemId) {
      const itemId = annot.itemId;
      const replaceId = annot.replaceId;
      uploadPhotos([blob], { endpoint: "/photos/upload", fields: { item_id: itemId, kind: annot.kind || "reference", caption: note || "" }, onEach: (p) => { addPhoto(itemId, p); if (isSandbox) refreshAuth(); if (replaceId) removePhoto(itemId, replaceId); } }).catch(handleUploadErr);
    }
  };
  const restorePhoto = (itemId, photo) => setItems((arr) => arr.map((it) => (it.id === itemId ? { ...it, photos: (it.photos || []).map((p) => (p.id === photo.id ? { ...p, ...photo } : p)) } : it)));
  const addPhoto = (itemId, photo) => setItems((arr) => arr.map((it) => (it.id === itemId ? { ...it, photos: [...(it.photos || []), photo] } : it)));
  const removePhoto = async (itemId, photoId) => {
    setItems((arr) => arr.map((it) => (it.id === itemId ? { ...it, photos: it.photos.filter((p) => p.id !== photoId) } : it)));
    try { await api.delete(`/photos/${photoId}`); } catch { /* ignore */ }
  };
  // Re-open an already-saved photo in the annotator; on save it uploads the marked-up
  // version and deletes the original (replaceId), so annotations stick after save.
  const annotatePhoto = async (itemId, photo) => {
    const kind = photo.kind === "proof" ? "proof" : "reference";
    try {
      const res = await fetch(imgUrl(photo.cloud_path, 1600, 90));
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const file = new File([blob], "photo.jpg", { type: blob.type || "image/jpeg" });
      setAnnot({ files: [file], mode: "item", itemId, kind, replaceId: photo.id });
    } catch { toast.error("Couldn't open that photo to edit — try again"); }
  };
  // Swap a photo for a fresh capture/gallery pick; replaces the old one on save.
  const replacePhoto = (itemId, photo) => {
    const kind = photo.kind === "proof" ? "proof" : "reference";
    setPhotoTarget({ itemId, kind, replaceId: photo.id });
    setAddSheet(true);
  };

  const shareUrl = stats?.token ? `${window.location.origin}/api/s/${stats.token}` : null;

  const shareNow = () => {
    const dueSuffix = report?.due_date ? `\n\nPlease complete by ${format(new Date(`${String(report.due_date).slice(0, 10)}T00:00:00`), "EEE, MMM d")}.` : "";
    const body = `Here's the punch list for ${job?.name || "your job"}${job?.location ? ` — ${job.location}` : ""}.${dueSuffix}\n\nOpen the link and for each item, snap a quick proof photo of the finished work. Tap "Send proof back" when you're done and I'll get it instantly.\n\n📸 A photo on each item is what closes it out.\n\nForward this to anyone on your crew helping out.`;
    if (title !== report.title) api.patch(`/reports/${reportId}`, { title }).catch(() => {});
    // Track the send: flip a Draft report to "Sent" so it no longer looks unsent.
    if (report?.status === "draft") {
      setReport((r) => (r ? { ...r, status: "sent" } : r));
      api.post(`/reports/${reportId}/mark-sent`).catch(() => {});
    }
    if (shareUrl && navigator.share) {
      navigator.share({ title: job?.name || "Send-Proof", text: body, url: shareUrl })
        .then(() => toast.success("Link sent ✓ — waiting on photos"))
        .catch(() => {});
      return;
    }
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copied ✓ — send it to whoever's doing the work")).catch(() => {});
    }
    setShareOpen(true);
  };
  const sharePdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await buildReportPdfBlob(reportId);
      const fname = `${(job?.name || "report").replace(/[^\w.-]+/g, "_")}.pdf`;
      const file = new File([blob], fname, { type: "application/pdf" });
      if (report?.status === "draft") { setReport((r) => (r ? { ...r, status: "sent" } : r)); api.post(`/reports/${reportId}/mark-sent`).catch(() => {}); }
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fname });
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = u; a.download = fname; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 15000);
        toast.success("PDF downloaded");
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) toast.error("Couldn't build the PDF — please tap again");
    } finally { setPdfBusy(false); }
  };
  const shareBeforeAfterPdf = async () => {
    setBaPdfBusy(true);
    try {
      const blob = await buildReportPdfBlob(reportId);
      const fname = `${(job?.name || "report").replace(/[^\w.-]+/g, "_")}_before-after.pdf`;
      const file = new File([blob], fname, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fname });
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = u; a.download = fname; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 15000);
        toast.success("Before & After PDF ready");
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) toast.error("Couldn't build the PDF — please tap again");
    } finally { setBaPdfBusy(false); }
  };
  const openShare = async () => {
    try { const { data } = await api.post(`/reports/${reportId}/share`); setStats(data.stats); } catch { /* ignore */ }
    setShareOpen(true);
  };

  const initialStats = useMemo(() => stats, [stats]);
  const doneCount = items.filter((i) => ["done", "na"].includes(i.status)).length;
  const issueCount = items.filter((i) => i.status === "issue").length;
  const orangeCount = items.filter((i) => !["done", "na", "issue"].includes(i.status) && (i.status === "sub_done" || (i.photos || []).some((p) => p.kind === "proof"))).length;
  const donePct = items.length ? Math.round(doneCount / items.length * 100) : 0;
  const hasResponses = items.some((it) => (it.photos || []).some((p) => p.kind === "proof"));
  const hasItems = items.length > 0;
  const hasPhotos = items.some((it) => (it.photos || []).length > 0);
  const openCount = stats?.total || 0;
  const alreadySent = !!report && report.status && report.status !== "draft";
  const allComplete = hasItems && doneCount === items.length;

  if (loading) return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div></AppShell>;

  if (!report) return (
    <AppShell onBack={() => goBackOrParent(navigate, `/jobs/${id}`)}>
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center" data-testid="report-load-error">
        <AlertTriangle className="h-8 w-8 text-amber-400" />
        <p className="max-w-xs text-sm text-muted-foreground">Couldn't load this report — likely a brief connection hiccup. Try again.</p>
        <Button onClick={() => { setLoading(true); load(); }} className="bg-emerald-500 hover:bg-emerald-600" data-testid="report-retry-btn">Retry</Button>
      </div>
    </AppShell>
  );

  return (
    <AppShell title={title || job?.name} onBack={() => goBackOrParent(navigate, `/jobs/${id}`)}>
      <div className="flex min-h-full flex-col px-4 pt-4 pb-32 sm:px-8">
        {/* Light title + compact status line */}
        <textarea
          data-testid="report-title-edit"
          value={title}
          rows={1}
          ref={autoGrow}
          onInput={(e) => autoGrow(e.target)}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== report.title && api.patch(`/reports/${reportId}`, { title })}
          className="mt-2 w-full resize-none overflow-hidden border-0 bg-transparent p-0 font-heading text-2xl font-extrabold leading-tight shadow-none outline-none focus:ring-0"
        />
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="report-status-line">
            <StatusPill status={report.status} />
            <span>· {items.length} {items.length === 1 ? "item" : "items"}{hasItems ? ` · ${donePct}%` : ""}</span>
            {issueCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-bold text-rose-500" data-testid="report-issue-count">
                <AlertTriangle className="h-3 w-3" /> {issueCount} flagged
              </span>
            )}
            <button
              onClick={openShare}
              data-testid="opens-chip"
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted/70"
            >
              <Eye className="h-3 w-3 text-emerald-500" /> {openCount} {openCount === 1 ? "open" : "opens"}
            </button>
            <DueChip
              dueDate={report.due_date}
              onChange={(val) => {
                setReport((r) => ({ ...r, due_date: val || null }));
                api.patch(`/reports/${reportId}`, { due_date: val }).catch(() => toast.error("Couldn't save due date"));
              }}
            />
          </div>
          {hasItems && <ProgressRing total={items.length} green={doneCount} orange={orangeCount} red={issueCount} size={52} stroke={5} />}
        </div>

        <SendLangSelect reportId={reportId} value={sendLang} onChange={setSendLang} />

        {report.worker_message && (
          <div
            data-testid="report-worker-message"
            className="mt-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-3.5"
          >
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-500">
              <MessageSquare className="h-3.5 w-3.5" />
              Note from {report.submitted_by_name || "the worker"}
            </div>
            <p className="mt-1 whitespace-pre-line text-[14px] leading-snug text-foreground">{report.worker_message}</p>
          </div>
        )}

        {!hasItems ? (
          /* Empty state — fills the screen, centered on the thumb */
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center" data-testid="report-empty-state">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10">
              <Camera className="h-8 w-8 text-emerald-500" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold">No items yet</h3>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">Tap Add Item and snap a photo of each thing that needs proof.</p>
            <button data-testid="add-item-empty-btn" onClick={() => openAddPhoto(null)} className="mt-6 flex h-12 w-full max-w-xs items-center justify-center gap-2 rounded-full bg-emerald-500 text-sm font-semibold text-white transition-transform active:scale-[0.98] hover:bg-emerald-600">
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-3">
              {items.map((it, i) => (
                <ItemCard key={it.id} index={i} item={it} onPatch={patchItem} onDelete={deleteItem} onOpenPhoto={setLightbox} onAddProof={(itemId) => openAddPhoto({ itemId, kind: "proof" })} onPhotoReplaced={restorePhoto} />
              ))}
            </div>

            {/* Add more items — each photo becomes a new item */}
            <div className="mt-6" ref={footerRef} data-testid="report-footer-actions">
              <button data-testid="add-item-btn" onClick={() => openAddPhoto(null)} className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-500/60 text-base font-semibold text-emerald-500 transition-transform active:scale-[0.99] hover:bg-emerald-500/10">
                <Plus className="h-5 w-5" /> Add Item
              </button>
            </div>
          </>
        )}
      </div>

      {/* Fixed bottom action bar — always reachable without scrolling a long list */}
      {hasItems && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-4 backdrop-blur-xl sm:px-8"
          style={{ paddingTop: "0.6rem", paddingBottom: "calc(env(safe-area-inset-bottom) + 0.6rem)" }}
          data-testid="report-action-bar"
        >
          <div className="mx-auto max-w-2xl">
            {allComplete ? (
              <button
                onClick={shareBeforeAfterPdf}
                disabled={baPdfBusy}
                data-testid="before-after-pdf-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-60"
              >
                {baPdfBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />} Share Before &amp; After PDF
              </button>
            ) : (
              <button
                onClick={() => setSendSheet(true)}
                disabled={busy || !hasPhotos}
                data-testid="send-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-base font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-50 disabled:shadow-none"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (alreadySent ? <Share2 className="h-5 w-5" /> : <Send className="h-5 w-5" />)} {alreadySent ? "Share again" : "Send"}
              </button>
            )}
          </div>
          {allComplete ? (
            <div className="mt-2 grid grid-cols-2 gap-2.5">
              <button onClick={() => goBackOrParent(navigate, `/jobs/${id}`)} data-testid="done-btn" className="flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-muted/40 text-sm font-bold text-foreground transition-all active:scale-[0.98] hover:bg-muted">
                <Check className="h-4 w-4 text-emerald-500" /> Done
              </button>
              <button onClick={openShare} data-testid="share-again-link" className="flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-muted/40 text-sm font-bold text-foreground transition-all active:scale-[0.98] hover:bg-muted">
                <Share2 className="h-4 w-4 text-emerald-500" /> Share link again
              </button>
            </div>
          ) : alreadySent ? (
            <button onClick={() => { setNudgeAuto(true); setShareOpen(true); }} data-testid="nudge-report-btn" className="mx-auto mt-1.5 flex items-center justify-center gap-1 text-center text-[12px] font-semibold text-amber-500 underline-offset-2 hover:underline">
              <Bell className="h-3.5 w-3.5" /> Nudge worker
            </button>
          ) : (!hasPhotos && (
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground" data-testid="send-hint">Add at least one photo before sending</p>
          ))}
        </div>
      )}

      <ShareSheet open={shareOpen} onOpenChange={(o) => { setShareOpen(o); if (!o) setNudgeAuto(false); }} reportId={reportId} initialStats={initialStats} jobName={job?.name} location={job?.location} dueDate={report?.due_date} autoNudge={nudgeAuto} onNudgeHandled={() => setNudgeAuto(false)} />
      {lightbox && <PhotoLightbox data={lightbox} onClose={() => setLightbox(null)} onAnnotate={annotatePhoto} onReplace={replacePhoto} onDelete={removePhoto} testId="report-lightbox" />}
      {dispatch && <PunchDispatch reportId={reportId} jobName={job?.name || ""} onClose={() => setDispatch(false)} />}

      <Sheet open={sendSheet} onOpenChange={setSendSheet}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="send-sheet">
          <SheetHeader><SheetTitle className="text-center">Send this list</SheetTitle></SheetHeader>
          <p className="mb-3 text-center text-xs text-muted-foreground">Choose how the proof link goes out.</p>
          <div className="space-y-3">
            <button
              onClick={() => { setSendSheet(false); shareNow(); }}
              data-testid="send-one-link"
              className="flex w-full items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-left transition-colors hover:bg-emerald-500/10"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white"><Send className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Send one link</div>
                <div className="text-xs text-muted-foreground">Text the whole list to one person — simplest</div>
              </div>
            </button>
            <button
              onClick={() => { setSendSheet(false); setDispatch(true); }}
              data-testid="send-by-trade"
              className="flex w-full items-center gap-3 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-muted"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600/15 text-blue-500"><Sparkles className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Split by trade</div>
                <div className="text-xs text-muted-foreground">We sort the items — send each trade only theirs (pick one or several)</div>
              </div>
            </button>
            <button
              onClick={() => { setSendSheet(false); sharePdf(); }}
              disabled={pdfBusy}
              data-testid="send-pdf"
              className="flex w-full items-center gap-3 rounded-2xl border border-border p-4 text-left transition-colors hover:bg-muted disabled:opacity-60"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-500/15 text-rose-500">{pdfBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">Send as PDF</div>
                <div className="text-xs text-muted-foreground">A polished PDF via your phone's share sheet</div>
              </div>
            </button>
          </div>
          <Button variant="outline" className="mt-4 h-11 w-full" onClick={() => setSendSheet(false)} data-testid="send-cancel">Cancel</Button>
        </SheetContent>
      </Sheet>
      {annot && (
        <PhotoAnnotator files={annot.files} title={annot.kind === "proof" ? "Add proof" : "Mark reference"} onSaveEach={handleAnnotSave} onDone={() => setAnnot(null)} onCancel={() => setAnnot(null)} />
      )}

      {/* Native label→input pickers: tapping opens the OS camera/gallery within the
          same gesture — far more reliable on mobile than a programmatic .click(). */}
      <Sheet open={addSheet} onOpenChange={(o) => { setAddSheet(o); if (!o) setPhotoTarget(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-[calc(env(safe-area-inset-bottom)+1rem)]" data-testid="add-photo-sheet">
          <SheetHeader><SheetTitle className="text-center">{photoTarget?.kind === "proof" ? "Add Proof Photo" : "Add Photo"}</SheetTitle></SheetHeader>
          <div className="mt-3 divide-y divide-border">
            <label data-testid="add-take-photo" className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors active:bg-muted">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><Camera className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold">Take Photo</span><span className="block text-xs text-muted-foreground">Rear camera</span></span>
              <input type="file" accept="image/*" capture="environment" hidden onChange={pickPhotos} />
            </label>
            <label data-testid="add-choose-gallery" className="flex w-full cursor-pointer items-center gap-3 py-4 text-left transition-colors active:bg-muted">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><ImageIcon className="h-5 w-5" /></span>
              <span className="flex-1"><span className="block font-semibold">Choose from Gallery</span><span className="block text-xs text-muted-foreground">Import existing</span></span>
              <input type="file" accept="image/*" multiple hidden onChange={pickPhotos} />
            </label>
          </div>
          <Button variant="outline" className="mt-3 h-11 w-full" onClick={() => setAddSheet(false)} data-testid="add-cancel">Cancel</Button>
        </SheetContent>
      </Sheet>
    </AppShell>
  );
}
